import { DatesProvider } from "dates/DatesContext";
import BreadcrumbDates from "components/core/blocks/BreadcrumbDates";
import { useParams } from "react-router-dom";
import Loading from "components/core/blocks/Loading";
import { useDates } from "dates/DatesContext";
import { useFetch } from "hooks/api";
import { useMemo, useState } from "react";
import { Container, Row, Col, Nav } from "react-bootstrap";
import { buildMetric } from "views/merchandise/metrics";
import { useModuleEnabled } from "redux/selectors/modules";
import { AGENCY_DASHBOARD_BREADCRUMB_SPEC, DASHBOARD_LINKS } from "utils/dashboardLinks";
import MetricsCard from "components/custom/merchandise/MetricsCard";
import { useAppSelector } from "../../redux/hooks";
import { ParentSummaryHeader } from "./components/ParentSummaryHeader";
import { ProductsTable } from "components/core/blocks/ProductsTable";
import { ProductCard } from "modules/pricingv2/ProductCard";
import { keepPreviousData, useMutation, useQueryClient } from "@tanstack/react-query";
import { SetValue, sendUpdates } from "modules/pricingv2/components/SetValue";
import { formatPercent } from "utils/formatNumber";
import { trellisPalette } from "components/custom/analytics/palettes";
import TrellisNavLink from "components/core/basic/NavLink";

// TODO factor this out into a utils file
const DASHBOARD_TYPES = {
  PRODUCT: "product",
  CATEGORY: "category",
  ROOT: "root",
};

const getDashboardTo = ({ resourceId, type }) => {
  let to;
  switch (type) {
    case DASHBOARD_TYPES.CATEGORY:
      to = `/user/merchandising/category/${resourceId}`;
      break;
    case DASHBOARD_TYPES.PRODUCT:
      to = `/user/merchandising/product/${resourceId}`;
      break;
    default:
      to = `/user/merchandising/dashboard`;
  }
  return to;
};

const ProductExpandedContent = ({ marketPlace, row }) => {
  return (
    <div style={{ backgroundColor: "#efefef" }} className="text-start">
      <ProductCard marketPlace={marketPlace} productId={row?.original?.product_id} />
    </div>
  );
};

const ProductsTab = ({ data, isOverviewDataLoading, marketPlace }) => {
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [valueType, setValueType] = useState("min_price");
  const [showSetValue, setShowSetValue] = useState(false);
  const channel = useAppSelector((state) => state.channel).currentChannel;

  const queryClient = useQueryClient();

  const updateProducts = useMutation({
    mutationFn: async (updates) => {
      await sendUpdates("product_id", updates, marketPlace, queryClient);
    },
  });

  const productById = useMemo(() => {
    const ids = {};
    if (data?.products) {
      for (const product of data.products) {
        ids[product.id] = product;
      }
    }
    return ids;
  }, [data]);

  const columnSpecs = [
    {
      key: "asp",
      compareKey: null,
      options: {
        checked: true,
        overrideCompare: true,
        overrideCompareAccessor: (d) => {
          return {
            total_units: d?.total_units,
            sales: d?.total_sales,
            total_units_prev: d?.total_units_prev,
            sales_prev: d?.total_sales_prev,
          };
        },
        overrideCompareFormatter: (v) => {
          const avgPrice = v.sales > 0 ? v.sales / v.total_units : 0;
          const preAvgPrice = v.total_units_prev > 0 ? v.sales_prev / v.total_units_prev : 0;
          const diff = (avgPrice - preAvgPrice) / preAvgPrice;
          let color = "#555";

          if (diff >= 0.03) {
            color = trellisPalette[12];
          } else if (diff <= -0.03) {
            color = trellisPalette[17];
          }
          return (
            <span style={{ color: "#555" }}>
              <span style={{ color: color }}>{formatPercent(diff, 2, true)}</span>
            </span>
          );
        },
      },
    },
    {
      key: "product_price",
      compareKey: "price_delta",
      options: {
        checked: true,
        isNegative: true,
        noColor: true,
        metric: {
          id: "product_price",
          format: "currency",
          accessor: (d) => d?.product_price,
          name: "Current Price",
        },
      },
    },
    {
      key: "min_price",
      compareKey: null,
      isStatic: true,
      options: {
        isStatic: true,
        editCell: true,
        format: "currency",
        minValue: 0,
        columnTitle: "Min",
        checked: true,
      },
    },
    {
      key: "max_price",
      compareKey: null,
      options: {
        isStatic: true,
        editCell: true,
        format: "currency",
        minValue: 0,
        columnTitle: "Max",
        checked: true,
      },
    },
    {
      key: "landed_cost",
      compareKey: null,
      options: {
        editCell: true,
        format: "currency",
        minValue: 0,
        columnTitle: "Landed",
        checked: false,
        toolTip: "The product's landed cost",
      },
    },
    {
      key: "shipping_cost",
      compareKey: null,
      options: {
        editCell: true,
        format: "currency",
        minValue: 0,
        columnTitle: "Shipping",
        checked: false,
        toolTip: "The product's shipping cost",
      },
    },
    {
      key: "sales",
      compareKey: "sales_percent",
      options: { checked: true },
    },
    { key: "units", compareKey: "units_percent", options: { checked: true } },
    {
      key: "profit",
      compareKey: "profit_delta",
      options: { checked: true },
    },
  ];

  if (isOverviewDataLoading) {
    return <Loading height={"300px"} fullPage={false} />;
  }

  return (
    <>
      <Row>
        <Col xs={12} className="mb-4 fs-standard">
          <ProductsTable
            selectedProducts={selectedProducts}
            onBulkChange={async (changes) => {
              const updates = {};
              Object.entries(changes).forEach(([id, changes]) => {
                updates[id] = Object.entries(changes)
                  .filter(([key]) => ["max_price", "min_price", "shipping_cost", "landed_cost"].includes(key))
                  .map(([key, value]) => [key, parseFloat(value)]);
              });
              updateProducts.mutate(updates);
            }}
            marketPlace={marketPlace}
            productData={data?.products}
            isLoading={isOverviewDataLoading}
            ExpandedContent={ProductExpandedContent}
            isSaving={updateProducts.isPending}
            onSelection={setSelectedProducts}
            cellContext={`parent`}
            customColumns={channel === "shopify" ? columnSpecs : null}
            bulkActionOptions={[
              [
                "Fixed Price",
                "Clear Fixed Price",
                "Minimum Price",
                "Maximum Price",
                "Shipping Cost",
                "Landed Cost",
              ].map((label) => ({
                label: label === "Clear Fixed Price" ? label : `Set ${label}`,
                action: () => {
                  setValueType(label);
                  setShowSetValue(true);
                },
              })),
            ]}
          />
        </Col>
      </Row>
      <SetValue
        showSetValue={showSetValue}
        setShowSetValue={setShowSetValue}
        valueType={valueType}
        selectedProducts={selectedProducts}
        productById={productById}
        marketPlace={marketPlace}
        setSelectedProducts={setSelectedProducts}
      />
    </>
  );
};

const ParentCard = ({ parentAsin, marketPlace, initialTab, data, isOverviewDataLoading, channel }) => {
  const { start, end, preStart, preEnd, group } = useDates();

  const {
    data: parentProduct,
    isLoading: isProductLoading,
    isError,
  } = useFetch(
    ["products", parentAsin, group, start, end, preStart, preEnd],
    `/api/gvads/parent/${parentAsin}`,
    {},
    {
      select: (res) => res.data,
      enabled: !!parentAsin,
    },
  );

  const isLoading = isProductLoading || isOverviewDataLoading;

  const overviewMetrics = useMemo(() => {
    if (channel === "shopify") {
      return [
        buildMetric(marketPlace, data, "sales", "sales_percent"),
        buildMetric(marketPlace, data, "units", "units_percent"),
        buildMetric(marketPlace, data, "margin", "margin_delta"),
      ];
    }

    return [
      buildMetric(marketPlace, data, "sales", "sales_percent"),
      buildMetric(marketPlace, data, "units", "units_percent"),
      // buildMetric(marketPlace, data, "total_order_count", null),
      buildMetric(marketPlace, data, "bsr", "bsr_percent", {
        metric: {
          totalOverrideAccessor: "last_bsr_rank",
        },
      }),
      buildMetric(marketPlace, data, "page_views", "page_views_percent"),

      buildMetric(marketPlace, data, "margin", "margin_delta"),
      buildMetric(marketPlace, data, "margin_wo_adspend", "margin_wo_adspend_delta"),

      buildMetric(marketPlace, data, "cost", "cost_percent"),
      buildMetric(marketPlace, data, "ad_sales", "ad_sales_percent"),
      buildMetric(marketPlace, data, "ad_spend", "ad_spend_percent"),
      buildMetric(marketPlace, data, "acos", "acos_delta"),
      buildMetric(marketPlace, data, "roas", "roas_delta"),
      buildMetric(marketPlace, data, "total_acos", "total_acos_delta"),
      buildMetric(marketPlace, data, "troas", "troas_delta"),
      buildMetric(marketPlace, data, "roms", "roms_delta"),

      buildMetric(marketPlace, data, "rating", "rating_percent"),

      buildMetric(marketPlace, data, "boosted_profit", null),
      buildMetric(marketPlace, data, "profit", "profit_percent"),
      buildMetric(marketPlace, data, "profit_wo_adspend", "profit_wo_adspend_percent"),

      buildMetric(marketPlace, data, "profit_per_page_view", "profit_per_page_view_percent"),
      buildMetric(marketPlace, data, "profit_per_page_view_wo_adspend", "profit_per_page_view_wo_adspend_percent"),
    ];
  }, [marketPlace, data, channel]);

  const [tab, setTab] = useState(initialTab ?? "product");

  if (isError) {
    return (
      <Row>
        <Col className="fs-3 p-5 text-center">
          The Parent ASIN you are looking for does not exist or is not available from Amazon.
        </Col>
      </Row>
    );
  }

  return (
    <>
      {isLoading ?
        <Loading height={"300px"} fullPage={false} />
      : <Row>
          <Col xs={12} className="mb-4 fs-standard">
            <MetricsCard
              initialSelectedMetric={["sales", "units"]}
              titleElement={<ParentSummaryHeader parentProduct={parentProduct} />}
              metrics={overviewMetrics}
              data={data}
              loading={isOverviewDataLoading}
              group={group}
              showMetricsSelector={channel !== "shopify"}
              id={"parentDashboard"}
              showAnnotations={true}
              context={"parent"}
              extendMetrics={channel !== "shopify"}
            >
              <Row>
                <Col xs={12} className="mt-5 ph-4 mb-3 border-bottom">
                  <Nav
                    variant="underline"
                    onSelect={(k) => {
                      return setTab(k);
                    }}
                    activeKey={tab}
                  >
                    <TrellisNavLink eventKey="product">Products</TrellisNavLink>
                  </Nav>
                </Col>
              </Row>
              <Row>
                <Col xs={12}>
                  {tab === "product" && (
                    <ProductsTab data={data} isOverviewDataLoading={isOverviewDataLoading} marketPlace={marketPlace} />
                  )}
                </Col>
              </Row>
            </MetricsCard>
          </Col>
        </Row>
      }
    </>
  );
};

// Wrapper which fetches data, generates a breadcrumb trail
// and adds the BreadcrumbDates component
const Parent = () => {
  const { parentAsin = null } = useParams();
  const marketPlace = useAppSelector((state) => state.marketPlace);
  const user = useAppSelector((state) => state.user);
  const channel = useAppSelector((state) => state.channel).currentChannel;

  const hasAgencyDashboardModule = useModuleEnabled("AGENCY_DASHBOARD");

  // Pulls dates from provided context
  const { start, end, preStart, preEnd, group } = useDates();

  // Data fetching

  const filters = {
    mp: marketPlace.marketPlace,
    date_group: group,
    start_date: start.format("YYYY-MM-DD"),
    end_date: end.format("YYYY-MM-DD"),
    pre_start_date: preStart.format("YYYY-MM-DD"),
    pre_end_date: preEnd.format("YYYY-MM-DD"),
    //   group_by: "product_id",
    parent_asin: parentAsin,
    channel: channel ?? "amazon",
  };

  const { data, isLoading: isOverviewDataLoading } = useFetch(
    ["product_report", marketPlace.marketPlace, start, end, preStart, preEnd, parentAsin],
    `/api/data_report/product_report`,
    filters,
    {
      select: (d) => d.data,
      placeholderData: keepPreviousData,
      enabled: !!parentAsin,
    },
  );

  const breadCrumbItems = [
    {
      name: "Merchandising Dashboard",
      href: getDashboardTo({
        resourceId: null,
        type: DASHBOARD_TYPES.ROOT,
      }),

      dropdownLinkOptions: DASHBOARD_LINKS,
      searchable: false,
      unsorted: true,
      dropdownSubtitle: "Dashboards",
    },
  ];

  if (hasAgencyDashboardModule) {
    breadCrumbItems.unshift(AGENCY_DASHBOARD_BREADCRUMB_SPEC);
  }

  return (
    <Container
      fluid
      style={{
        minHeight: "calc(100vh - 150px)",
        background: "#fefefe",
      }}
    >
      <Row>
        <Col xs={12} className="mb-4">
          <BreadcrumbDates items={breadCrumbItems} />
        </Col>
      </Row>
      <Row>
        <Col xs={12} className="mb-4 px-3">
          <ParentCard
            parentAsin={parentAsin}
            marketPlace={marketPlace}
            user={user}
            data={data}
            isOverviewDataLoading={isOverviewDataLoading}
            channel={channel}
          />
        </Col>
      </Row>
    </Container>
  );
};

const MerchParent = () => {
  return (
    <DatesProvider>
      <Parent />
    </DatesProvider>
  );
};

export default MerchParent;
